import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import { Box, Button, Chip, CircularProgress, Collapse, IconButton, Menu, MenuItem, Paper, Stack, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AddIcon from "@mui/icons-material/Add";
import { LoginContext } from "../../context/login-context";
import useStatements from "../../hooks/use-statements";
import useDeleteStatement from "../../hooks/use-delete-statement";
import useDownloadStatement from "../../hooks/use-download-statement";
import StatementPdf from "./statement-pdf";
import { statementToPdfProps } from "./statement-to-pdf-props";

/* Group statements by parent account (group key). */
const groupByAccount = (statements) => {
  const groups = {};
  statements.forEach((s) => {
    if (!groups[s.account]) groups[s.account] = [];
    groups[s.account].push(s);
  });
  return groups;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2
  }).format(value || 0);

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/* Map a backend statement row to the shape the list renders. */
const toDisplayStatement = (s) => ({
  id: s.id,
  account: s.primary_parent_account_name || s.scope_label || "Other",
  scopeLabel: s.scope_label || s.primary_parent_account_name || "—",
  reference: s.reference,
  name: s.name || null,
  invoiceCount: s.invoice_count || 0,
  total: s.grand_total,
  rebate: s.grand_rebate,
  createdAt: formatDate(s.created_at),
  isConsolidated: s.scope_type === "CONSOLIDATED"
});

const isConsolidatedStatement = (statement) => statement.isConsolidated;

/* A single statement list row (shared by consolidated + grouped sections). */
const StatementRow = ({ statement, onMenuOpen, onDownload, downloading }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1.4fr 1.2fr 1fr 1fr 1fr 130px",
      columnGap: 2,
      pl: 5,
      pr: 3,
      py: 1.5,
      borderBottom: "1px solid #f3f4f6",
      alignItems: "center",
      "&:hover": { bgcolor: "#fafafa" }
    }}
  >
    <Box>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
        {statement.scopeLabel}
      </Typography>
      <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.25 }}>
        {statement.invoiceCount} {statement.invoiceCount === 1 ? "invoice" : "invoices"}
      </Typography>
    </Box>
    <Box>
      <Typography sx={{ fontSize: "0.85rem", color: "#374151", fontWeight: 600 }}>
        {statement.reference}
      </Typography>
      {statement.name && (
        <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.25 }}>
          {statement.name}
        </Typography>
      )}
    </Box>
    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{formatCurrency(statement.total)}</Typography>
    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#10b981" }}>{formatCurrency(statement.rebate)}</Typography>
    <Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>{statement.createdAt}</Typography>
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
      <Button
        size="small"
        variant="outlined"
        onClick={() => onDownload(statement.id)}
        disabled={downloading}
        sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem", borderRadius: 1.5, px: 1.5, minWidth: 0 }}
        startIcon={
          downloading
            ? <CircularProgress size={14} thickness={5} />
            : <DownloadIcon sx={{ fontSize: 16 }} />
        }
      >
        {downloading ? "…" : "Download"}
      </Button>
      <IconButton
        size="small"
        aria-label={`More actions for statement`}
        onClick={(e) => onMenuOpen(e, statement.id)}
        sx={{
          width: 28,
          height: 28,
          color: "#9ca3af",
          "&:hover": { bgcolor: "#f3f4f6", color: "#374151" },
          "&:focus-visible": { outline: "2px solid #6366f1", outlineOffset: 1 }
        }}
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Stack>
  </Box>
);

const Statements = () => {
  const navigate = useNavigate();
  const { state: userData } = useContext(LoginContext);
  const { statements: rawStatements, statementsLoading, statementsError, refetchStatements } = useStatements();
  const statements = rawStatements.map(toDisplayStatement);
  const consolidatedStatements = statements.filter(isConsolidatedStatement);
  const grouped = groupByAccount(statements.filter((statement) => !isConsolidatedStatement(statement)));
  const groupedEntries = Object.entries(grouped);
  const hasStatements = statements.length > 0;
  const defaultGroupExpanded = groupedEntries.length <= 2;
  const isAdminFinanceView = ["SUPER_ADMIN", "VALMONT_FINANCE", "VALMONT_REP"].includes(userData?.user_role);
  const parentAccountName =
    userData?.associatedAcctIds?.temp_strat_acct_name ||
    userData?.associatedAcctIds?.temp_sub_entity_acct_name ||
    userData?.strategic_acct?.name ||
    userData?.farm?.strategic_acct?.name ||
    "Your account";
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuStatementId, setMenuStatementId] = useState(null);
  const [isConsolidatedExpanded, setIsConsolidatedExpanded] = useState(true);
  // Per-group override; undefined falls back to defaultGroupExpanded at render
  // time so groups behave correctly once async data has loaded.
  const [expandedGroups, setExpandedGroups] = useState({});
  const [downloadingId, setDownloadingId] = useState(null);

  const { mutate: deleteStatement } = useDeleteStatement({
    onSuccess: () => refetchStatements()
  });
  const { mutateAsync: fetchStatement } = useDownloadStatement();

  const handleMenuOpen = (event, statementId) => {
    setMenuAnchor(event.currentTarget);
    setMenuStatementId(statementId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuStatementId(null);
  };

  const handleDelete = () => {
    if (menuStatementId) deleteStatement(menuStatementId);
    handleMenuClose();
  };

  const handleDownload = async (statementId) => {
    if (downloadingId) return;
    setDownloadingId(statementId);
    try {
      const statement = await fetchStatement(statementId);
      if (!statement) throw new Error("Statement not found");

      const props = statementToPdfProps(statement);
      const blob = await pdf(<StatementPdf {...props} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileLabel = (statement.reference || "statement").toLowerCase();
      a.download = `statement-${fileLabel}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download statement", err);
    } finally {
      setDownloadingId(null);
    }
  };

  const isGroupExpanded = (account) => expandedGroups[account] ?? defaultGroupExpanded;

  const toggleGroup = (account) => {
    setExpandedGroups((prev) => ({ ...prev, [account]: !isGroupExpanded(account) }));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%", maxWidth: "100%" }}>

      {/* ─── OPTION A — Flat list (no grouping) ─── */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, letterSpacing: -0.5 }}>
            Statements
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
            Your generated monthly invoice reports.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          {/* TODO: implement filter functionality */}
          {/* <Button
            variant="outlined"
            disableElevation
            startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
            sx={{ borderRadius: 2, px: 2, textTransform: "none", fontWeight: 600, color: "#374151", borderColor: "#e5e7eb" }}
          >
            Filter
          </Button> */}
          <Button
            variant="contained"
            disableElevation
            onClick={() => navigate("/statements/build")}
            sx={{ borderRadius: 2, px: 2.5, textTransform: "none", fontWeight: 600 }}
          >
            + Create statement
          </Button>
        </Stack>
      </Stack>


      {statementsLoading && (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: "0.85rem", color: "#9ca3af", mt: 2 }}>
            Loading statements…
          </Typography>
        </Stack>
      )}

      {!statementsLoading && statementsError && (
        <Paper
          elevation={0}
          sx={{ border: "1px solid #e5e7eb", borderRadius: 3, bgcolor: "#fff", px: 4, py: 8 }}
        >
          <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#111827" }}>
              Couldn't load statements.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => refetchStatements()}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Try again
            </Button>
          </Stack>
        </Paper>
      )}

      {!statementsLoading && !statementsError && !hasStatements && isAdminFinanceView && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: 3,
            bgcolor: "#fff",
            px: { xs: 3, md: 4 },
            py: { xs: 6, md: 9 }
          }}
        >
          <Stack alignItems="center" spacing={2.5} sx={{ maxWidth: 440, mx: "auto", textAlign: "center" }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#eef2ff",
                color: "#6366f1"
              }}
            >
              <DescriptionOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, color: "#111827", letterSpacing: -0.3 }}>
                No statements yet
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#6b7280", mt: 1, lineHeight: 1.7 }}>
                Create one to get started.
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#9ca3af", mt: 0.25, lineHeight: 1.7 }}>
                Grouped by account.
              </Typography>
            </Box>

            <Button
              variant="contained"
              disableElevation
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={() => navigate("/statements/build")}
              sx={{ borderRadius: 2, px: 2.5, mt: 0.5, textTransform: "none", fontWeight: 600 }}
            >
              Create statement
            </Button>
          </Stack>
        </Paper>
      )}

      {!statementsLoading && !statementsError && !hasStatements && !isAdminFinanceView && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "#fff"
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{ px: 3, py: 1.25, bgcolor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
              {parentAccountName}
            </Typography>
          </Stack>

          <Stack alignItems="center" spacing={1.75} sx={{ px: 3, py: { xs: 5, md: 6 }, textAlign: "center" }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#eef2ff",
                color: "#6366f1"
              }}
            >
              <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#111827" }}>
                No statements yet
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "#6b7280", mt: 0.75, lineHeight: 1.7 }}>
                Statements you create will appear here.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* ─── OPTION B — Grouped by account (original) ─── */}
      {hasStatements && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "#fff"
          }}
        >
        {/* Column headers */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1.2fr 1fr 1fr 1fr 130px",
            columnGap: 2,
            pl: 5,
            pr: 3,
            py: 1.5,
            bgcolor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb"
          }}
        >
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Account</Typography>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Statement</Typography>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Total</Typography>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Rebate</Typography>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Created</Typography>
          <Box />
        </Box>

        {consolidatedStatements.length > 0 && (
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 3, py: 1.25, bgcolor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <IconButton
                  size="small"
                  onClick={() => setIsConsolidatedExpanded((prev) => !prev)}
                  aria-label={`${isConsolidatedExpanded ? "Collapse" : "Expand"} Consolidated / Custom Reports`}
                  sx={{
                    width: 24,
                    height: 24,
                    color: "#6b7280",
                    "&:hover": { bgcolor: "#f3f4f6" }
                  }}
                >
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 18,
                      transform: isConsolidatedExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                      transition: "transform 150ms ease"
                    }}
                  />
                </IconButton>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                  Consolidated / Custom Reports
                </Typography>
              </Stack>
            </Stack>

            <Collapse in={isConsolidatedExpanded} timeout="auto" unmountOnExit>
              {consolidatedStatements.map((statement) => (
                <StatementRow
                  key={statement.id}
                  statement={statement}
                  onMenuOpen={handleMenuOpen}
                  onDownload={handleDownload}
                  downloading={downloadingId === statement.id}
                />
              ))}
            </Collapse>
          </Box>
        )}

        {groupedEntries.map(([account, statements]) => (
          <Box key={account}>
            {/* Group header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 3, py: 1.25, bgcolor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <IconButton
                  size="small"
                  onClick={() => toggleGroup(account)}
                  aria-label={`${isGroupExpanded(account) ? "Collapse" : "Expand"} ${account}`}
                  sx={{
                    width: 24,
                    height: 24,
                    color: "#6b7280",
                    "&:hover": { bgcolor: "#f3f4f6" }
                  }}
                >
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 18,
                      transform: isGroupExpanded(account) ? "rotate(0deg)" : "rotate(-90deg)",
                      transition: "transform 150ms ease"
                    }}
                  />
                </IconButton>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                  {account}
                </Typography>
                <Chip
                  label={`${statements.length}`}
                  size="small"
                  sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#e5e7eb" }}
                />
              </Stack>
            </Stack>

            <Collapse in={isGroupExpanded(account)} timeout="auto" unmountOnExit>
              {/* Indented rows */}
              {statements.map((statement) => (
                <StatementRow
                  key={statement.id}
                  statement={statement}
                  onMenuOpen={handleMenuOpen}
                  onDownload={handleDownload}
                  downloading={downloadingId === statement.id}
                />
              ))}
            </Collapse>
          </Box>
        ))}
        </Paper>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { borderRadius: 2, minWidth: 140, mt: 0.5 }
          }
        }}
      >
        <MenuItem
          onClick={handleDelete}
          sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#dc2626", "&:hover": { bgcolor: "#fef2f2" } }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Statements;
